import {
  CurrentUserSchema,
  SignInOutcomeSchema,
  type CurrentUser,
  type Gender,
  type SignInOutcome,
} from "@talent/protocol";
import { z } from "zod";

/**
 * Account calls.
 *
 * There is no token handling here on purpose. The session lives in an httpOnly cookie the
 * browser attaches and this code cannot read, which is the property that makes an XSS
 * unable to steal it. `credentials: "same-origin"` is stated explicitly rather than relied
 * on as the default, because the default being correct is not the same as the intent being
 * recorded.
 */

const BASE = import.meta.env["VITE_API_BASE"] ?? "";

/** Server-side refusals the account screens act on differently. */
export type AuthFailure =
  | "bad-phone"
  | "bad-code"
  | "cooldown"
  | "quota"
  | "sms-failed"
  | "deleted"
  /** This number already has an application waiting for a decision. */
  | "already-applied"
  /** Somebody has applied with that national id — which is not something they can fix here. */
  | "national-id-taken"
  | "unknown";

export class AuthError extends Error {
  constructor(
    readonly failure: AuthFailure,
    /** Present on `cooldown`, so the screen can count down instead of just refusing. */
    readonly retryAfterSeconds?: number,
  ) {
    super(failure);
    this.name = "AuthError";
  }
}

/**
 * Who is signed in, or nobody.
 *
 * One shape for both roles, because there is one sign-in flow for both. `role` is what the shell
 * reads to decide which surface to show, and `profileId` is present only for a student — it is the
 * learner record the recorder writes against, and a teacher does not have one.
 */
const MeSchema = z.object({ user: CurrentUserSchema.nullable() });
export type Me = CurrentUser | null;

/**
 * The server names its refusals with a `code` field.
 *
 * Read from the body rather than inferred from the status, because 429 means two different
 * things — wait a minute, or come back tomorrow — and someone told the wrong one either
 * waits pointlessly or keeps pressing a button that will not work.
 */
const RefusalSchema = z.object({
  code: z.string().optional(),
  retryAfterSeconds: z.number().optional(),
});

const BY_CODE: Record<string, AuthFailure> = {
  "bad-phone": "bad-phone",
  "bad-code": "bad-code",
  cooldown: "cooldown",
  quota: "quota",
  "sms-failed": "sms-failed",
  deleted: "deleted",
  "already-applied": "already-applied",
  "national-id-taken": "national-id-taken",
};

async function toAuthError(res: Response): Promise<AuthError> {
  const parsed = RefusalSchema.safeParse(await res.json().catch(() => ({})));
  const body = parsed.success ? parsed.data : {};
  // `?? "unknown"` alone would let an empty-string code through as a falsy-but-present
  // value, so the lookup is explicit.
  const failure = body.code !== undefined ? BY_CODE[body.code] : undefined;
  return new AuthError(failure ?? "unknown", body.retryAfterSeconds);
}

async function submit(path: string, body: unknown): Promise<Response> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toAuthError(res);
  return res;
}

export interface CodeRequested {
  /** Seconds before another code may be asked for. Drives the resend countdown. */
  retryAfterSeconds: number;
}

const RequestedSchema = z.object({ retryAfterSeconds: z.number() });

/**
 * Asks for a code.
 *
 * The same call whether the number has an account or not — which is what makes the
 * response unable to reveal whether it does.
 */
export async function requestCode(phone: string): Promise<CodeRequested> {
  const res = await submit("/v1/auth/otp/request", { phone });
  return RequestedSchema.parse(await res.json());
}

/**
 * Submits a code, and gets back one of four answers.
 *
 * A correct code is no longer the same thing as an account (ADR-012), so this returns the whole
 * outcome rather than a role. Three of the four are not failures and must not be thrown: somebody
 * waiting on a decision typed the right six digits, and telling them the code was wrong would send
 * them to retype it forever.
 *
 * On `signed-in` the cookie is already set and the account comes with it, so nothing needs to ask
 * again before choosing a screen.
 */
export async function verifyCode(phone: string, code: string): Promise<SignInOutcome> {
  const res = await submit("/v1/auth/otp/verify", { phone, code });
  return SignInOutcomeSchema.parse(await res.json());
}

/** What a teacher fills in to ask for a class. The national id is checked before it is sent. */
export interface Application {
  phone: string;
  code: string;
  firstName: string;
  lastName: string;
  nationalId: string;
  schoolName: string;
  gender: Gender;
}

/**
 * Applies to teach.
 *
 * The code goes with the form rather than buying a session first: there is no account to have a
 * session for, and it is what keeps the queue from being sprayed with rows that each cost a person's
 * attention to read.
 */
export async function applyToTeach(application: Application): Promise<void> {
  await submit("/v1/registrations", application);
}

/**
 * Correcting your own name and national id.
 *
 * Not the phone: it is the credential, and moving an account to a new number with no proof that the
 * number's holder agreed is the one edit this product cannot offer. Not the school either — approval
 * was granted on it, so changing it would undo somebody's decision.
 */
export async function updateMe(input: {
  firstName: string;
  lastName: string;
  nationalId: string;
}): Promise<CurrentUser> {
  const res = await fetch(`${BASE}/v1/auth/me`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await toAuthError(res);
  return MeSchema.parse(await res.json()).user as CurrentUser;
}

export async function signOut(): Promise<void> {
  await fetch(`${BASE}/v1/auth/logout`, {
    method: "POST",
    credentials: "same-origin",
  });
}

export async function fetchMe(): Promise<Me> {
  const res = await fetch(`${BASE}/v1/auth/me`, {
    headers: { accept: "application/json" },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`account lookup failed: ${res.status}`);
  return MeSchema.parse(await res.json()).user;
}
